import { ChangeDetectionStrategy, Component, ElementRef, inject, Injector } from '@angular/core';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ESampleType, Product } from '../../catalog/product/product.decorator';
import { EdependencyLevel } from '@df/catalog/model';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import groupBy from 'lodash-es/groupBy';
import { IProductDataV2 } from '@df/catalog/product/product';
import { SamplesFilterComponent } from './filter/samples-filter.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import range from 'lodash-es/range';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { StickyPanelDirective } from '@df/legacy/ui/sticky/sticky-panel.directive';
import { pageHeaderStickyOffForce } from '../../page/header/page-header.component';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { FileInputComponent } from '../../ui/form/file-input/file-input.component';
import { ICustomformField } from '@df/catalog/customform/customform';
import { FormsModule } from '@angular/forms';
import { FormMuiFieldComponent } from '../../common/form/mui-field/form-mui-field.component';
import { FormFieldComponent } from '../../common/form/field/form-field.component';
import { CheckboxComponent } from '../../common/form/checkbox/checkbox.component';
import { RadioGroupComponent } from '../../common/form/radio/group/radio-group.component';
import { ICallableObject, ISubmitFormOptions } from '@df/api/request/request.service';
import { RouterLink } from '@angular/router';
import { IAddressData } from '@df/session/api/address-book/address';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { AbstractLocalCustomformComponentDef } from '../../catalog/customform/abstract-local-customform.component.def';
import { CustomformAddressFieldsetComponent } from '../../catalog/customform/address-fieldset/customform-address-fieldset.component';
import { GoogleRecaptchaDirective } from '@df/module-google-recaptcha/google-recaptcha.directive';
import { ResultComponent } from '../../common/result/result.component';
import { RadioGroupItemComponent } from '../../common/form/radio/group/item/radio-group-item.component';

export namespace NSamplesComponent {
    export interface IComponentData extends NICmsContentComponent.IData {
        title?: string;
        yourSelectionTitle?: string;
        yourSelectionDescription?: string;
        maxSamples?: number;
        formTitle?: string;
        form_identifier?: string;
        successTitle?: string;
        successDescription?: string;
        filterAttributes?: string[];
        groupByAttribute?: string;
        fieldId?: string;
    }

    export enum EStep {
        selection = 'selection',
        form = 'form',
        success = 'success'
    }

    export interface IGroup {
        fabricStyle: number;
        products: Product<IProductDataV2>[];
        title: string;
    }
}

@ICmsComponent<SamplesComponent>({
    id: 'samples',
    name: 'Samples',
    tags: ['samples', 'listing'],
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'Fabric Swatch Request'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'yourSelectionTitle',
            title: 'Your Selection Title',
            default: 'Your selection'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'yourSelectionDescription',
            title: 'Your Selection Description',
            default: 'Select up to 6 free samples'
        },
        <NICmsContentField.INumberField>{
            type: NICmsContentField.EFieldType.number,
            key: 'maxSamples',
            title: 'Max Samples Qty',
            default: 6,
            min: 1
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'formTitle',
            title: 'Form Title',
            default: 'Please fill out and submit the below form:'
        },
        {
            key: 'form_identifier',
            type: NICmsContentField.EFieldType.text,
            title: 'Form Identifier'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'fieldId',
            title: 'Selected Products Field ID',
            required: true
        },

        {
            key: 'successTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Success Title',
            default: 'Thank You! Swatch Request Received'
        },
        {
            key: 'successDescription',
            type: NICmsContentField.EFieldType.text,
            title: 'Success Description',
            default: 'Check your email inbox for your confirmation.'
        },
        <NICmsContentField.IRelationField>{
            key: 'groupByAttribute',
            type: NICmsContentField.EFieldType.relation,
            title: 'Group By Attribute',
            required: true,
            options: {
                type: 'attribute'
            },
            default: 'fabric_style'
        },
        <NICmsContentField.IRelationField>{
            key: 'filterAttributes',
            type: NICmsContentField.EFieldType.relation,
            title: 'Filter Attributes',
            options: {
                type: 'attribute',
                multiple: true
            },
            default: ['fabric_style'],
            required: true
        }
    ]
})
@Component({
    imports: [
        DfModuleImageModule,
        ICmsClientModule,
        GridModule,
        StickyPanelDirective,
        SamplesFilterComponent,
        TranslationModule,
        ButtonStyleComponent,
        DfUiCarouselModule,
        CommonModule,
        FormsModule,
        FormMuiFieldComponent,
        FormFieldComponent,
        CheckboxComponent,
        RadioGroupComponent,
        FileInputComponent,
        CustomformAddressFieldsetComponent,
        RouterLink,
        GoogleRecaptchaDirective,
        ResultComponent,
        RadioGroupItemComponent
    ],
    selector: 'cms-samples',
    templateUrl: './samples.html',
    styleUrl: './samples.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SamplesComponent extends AbstractLocalCustomformComponentDef implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NSamplesComponent.IComponentData;

    _icmsContent$!: BehaviorSubject<NSamplesComponent.IComponentData>;

    protected groups: NSamplesComponent.IGroup[] = [];

    protected currentStep = NSamplesComponent.EStep.selection;

    protected listing: ProductListing | undefined;

    protected slots = range(this.maxSamplesQty);

    protected selectedProducts: (Product<IProductDataV2> | undefined)[] = [];

    protected deliveryAddress: string[] = [];

    private listingSubscription = new Subscription();

    private selectedProductsSet = new Set<number>();

    private _maxSamplesQty = 6;

    private _groupByAttrCode?: string;

    private _filtersAttrsCodes: string[] = [];

    private timeoutService = inject(TimeoutService);

    private loadDebounced = this.timeoutService.debounce(() => this.load(), 200);

    readonly injector = inject(Injector);

    private elementRef = inject(ElementRef);

    private productListingFactory = inject(ProductListingFactory);

    private scrollToService = inject(ScrollToService);

    override ngOnInit(): void {
        super.ngOnInit();

        if (this._icmsContent$) {
            this.subscriptions.add(
                this._icmsContent$.subscribe(() => {
                    this.groupByAttrCode = this.component?.groupByAttribute;
                    this.filtersAttrsCodes = this.component?.filterAttributes;
                    const maxSamplesQty = this.component?.maxSamples;
                    if (maxSamplesQty) {
                        this.maxSamplesQty = Math.floor(maxSamplesQty);
                    }
                    if (this.component?.form_identifier) {
                        this.identifier = this.component.form_identifier;
                        this.loadForm();
                    }
                })
            );
        }
        pageHeaderStickyOffForce.next(this.stickyOff);
        this.app.client.obs.breakpoint.subscribe(() => {
            pageHeaderStickyOffForce.next(this.stickyOff);
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        pageHeaderStickyOffForce.next(false);
        this.listingSubscription?.unsubscribe();
    }

    protected get display(): boolean {
        return !!(this.listing?.models?.length && this.groups?.length && this.model?.data);
    }

    private get stickyOff(): boolean {
        return this.app.client.sizeId > 1;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Helpers
    ////////
    ////////////////////////////////////////////

    protected get pcSlides(): number {
        return 4.25;
    }

    protected get pcSlidesCeil(): number {
        return Math.ceil(this.pcSlides);
    }

    ////////////////////////////////////////////
    ////////
    ////////    Steps logic
    ////////
    ////////////////////////////////////////////

    protected get isSelection(): boolean {
        return this.currentStep === NSamplesComponent.EStep.selection;
    }

    protected get isForm(): boolean {
        return this.currentStep === NSamplesComponent.EStep.form;
    }

    protected get isSuccess(): boolean {
        return this.currentStep === NSamplesComponent.EStep.success;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Selection logic
    ////////
    ////////////////////////////////////////////

    protected get isFull(): boolean {
        return this.selectedProductsSet.size >= this.maxSamplesQty;
    }

    protected get submitAllowed(): boolean {
        return this.selectedProductsSet.size > 0;
    }

    protected removeProduct(product: Product<IProductDataV2>): void {
        if (this.isSelected(product.id)) {
            const index = this.selectedProducts.indexOf(product);
            if (index > -1) {
                this.selectedProducts[index] = undefined;
            }
            this.selectedProductsSet.delete(product.id);
        }
        this.detectChanges();
    }

    protected addProduct(product: Product<IProductDataV2>): void {
        if (!this.isSelected(product.id)) {
            const firstEmptyIndex = this.selectedProducts.findIndex(i => !i);
            if (this.selectedProducts.length < this.maxSamplesQty || firstEmptyIndex > -1) {
                if (firstEmptyIndex > -1) {
                    this.selectedProducts[firstEmptyIndex] = product;
                } else {
                    this.selectedProducts.push(product);
                }
                this.selectedProductsSet.add(product.id);
            }
        }
        this.detectChanges();
    }

    protected isSelected(productId: number): boolean {
        return this.selectedProductsSet.has(productId);
    }

    protected set maxSamplesQty(qty: number) {
        if (qty) {
            this._maxSamplesQty = qty;
            if (this.selectedProducts.length > this.maxSamplesQty) {
                this.selectedProducts = this.selectedProducts.filter(i => !!i).slice(0, this.maxSamplesQty);
                this.selectedProductsSet = new Set(this.selectedProducts.map(i => i!.id));
            }
            this.slots = range(this.maxSamplesQty);
            this.detectChanges();
        }
    }

    protected get maxSamplesQty(): number {
        return this._maxSamplesQty;
    }

    private load(): void {
        this.app.resources.product
            .find({ verbosity: 2, filter: { sample_type: ESampleType.fabric } }, { safe: true, dependencyLevel: EdependencyLevel.listing })
            .then(products => {
                if (products?.length) {
                    const productsIds = (products as Product<IProductDataV2>[])
                        .filter(i => i.data?.fabric_style)
                        .map(product => product.id);
                    this.listing = this.productListingFactory.create(
                        { type: 'product', storage: false, infScrollInit: productsIds.length, infScrollStep: 1 },
                        productsIds,
                        {
                            filtersDefinitions: this.filtersAttrsCodes
                        }
                    );
                    if (this.listing) {
                        this.listingSubscription = this.listing.subscribe(() => {
                            this.onListingUpdate();
                        });
                        this.onListingUpdate();
                    }
                    this.detectChanges();
                }
            });
    }

    private set filtersAttrsCodes(codes: string[] | undefined) {
        const was = this._filtersAttrsCodes;
        this._filtersAttrsCodes = codes || [];
        if (JSON.stringify(was) !== JSON.stringify(this._filtersAttrsCodes)) {
            this.loadDebounced();
        }
    }

    private get filtersAttrsCodes(): string[] {
        return this.component?.filterAttributes || [];
    }

    private set groupByAttrCode(code: string | undefined) {
        if (code && code !== this._groupByAttrCode) {
            this._groupByAttrCode = code;
            this.loadDebounced();
        }
    }

    private get groupByAttrCode(): string | undefined {
        return this._groupByAttrCode;
    }

    private onListingUpdate(): void {
        if (this.listing?.models) {
            const attr = this.groupByAttrCode ? this.app.resources.attribute.getExisting(this.groupByAttrCode) : undefined;
            if (attr) {
                const grouped = groupBy(this.listing.models, i => i.data?.[this.groupByAttrCode!]);
                this.groups = Object.entries(grouped)
                    .map(([attributeValue, products]) => {
                        const attrOption = attr.getOptionByValue(parseInt(attributeValue));
                        if (attrOption && products.length) {
                            return {
                                fabricStyle: attrOption.value,
                                products,
                                title: attrOption.label
                            };
                        }
                    })
                    .filter(i => !!i?.products?.length) as NSamplesComponent.IGroup[];
            } else {
                this.groups = [];
            }
            this.detectChanges();
        }
    }

    protected selectionAction(): void {
        if (this.currentStep === NSamplesComponent.EStep.selection) {
            this.currentStep = NSamplesComponent.EStep.form;
        } else if (this.currentStep === NSamplesComponent.EStep.form) {
            this.currentStep = NSamplesComponent.EStep.selection;
        }
        if (this.form) {
            this.form.submitAttempt = false;
            this.form.ngSubmit.emit();
        }
        this.scrollToService.scrollTo(0, { behavior: 'smooth' });
        this.detectChanges();
    }

    protected get selectionCTATitle(): string {
        if (this.currentStep === NSamplesComponent.EStep.form) {
            return 'change selection';
        }
        return 'confirm selections';
    }

    ////////////////////////////////////////////
    ////////
    ////////    Form logic
    ////////
    ////////////////////////////////////////////

    override get submitFormOptions(): ISubmitFormOptions {
        return { errorScrollContainer: this.elementRef.nativeElement };
    }

    protected override onSubmitSuccess(response?: any): void {
        this.deliveryAddress = this.getDeliveryAddress();
        this.scrollToService.scrollTo(0, { behavior: 'smooth' });
        super.onSubmitSuccess(response);
        this.currentStep = NSamplesComponent.EStep.success;
        this.detectChanges();
    }

    protected override getSubmitAction(): ICallableObject<Partial<{}>> {
        const submitAction = super.getSubmitAction();

        const productsSkusFieldID = this.component?.fieldId;
        if (submitAction?.data && productsSkusFieldID) {
            submitAction.data[productsSkusFieldID] = this.selectedProducts
                .map(i => i?.data?.sku)
                .filter(i => !!i)
                .join(', ');
        }

        return submitAction;
    }

    private getDeliveryAddress(): string[] {
        const parts: string[] = [];
        const addressField = this.getFieldByType('address');
        if (addressField && this.data) {
            const addressData = this.data[addressField.cid] as IAddressData;
            if (addressData) {
                if (addressData.street_1) {
                    parts.push(addressData.street_1);
                }
                const firstNameField = this.getFieldByLabel('First Name');
                const lastNameField = this.getFieldByLabel('Last Name');
                if (firstNameField || lastNameField) {
                    const name = [firstNameField, lastNameField]
                        .filter(i => !!i)
                        .map(i => this.data[i.cid])
                        .filter(i => !!i)
                        .join(' ');
                    if (name) {
                        parts.push(name);
                    }
                }
                if (addressData.city) {
                    parts.push(addressData.city);
                }
                if (addressData.postcode) {
                    parts.push(addressData.postcode);
                }
            }
        }

        return parts.map(i => i.trim()).filter(i => !!i);
    }

    private getFieldByType(type: string): ICustomformField | undefined {
        return this.model?.data?.fields?.find(i => i.field_type === type);
    }
}
